import React, { useMemo } from "react";

import type { DashboardData } from "../../types";
import { formatINR, formatSellThroughPct } from "../../utils/format";
import "./ShowHealthRail.css";

type RailSection = "health" | "budget" | "sellthrough";

type Props = {
  data: DashboardData;
  compact?: boolean;
  /** Which cards to render. Defaults to both. */
  sections?: RailSection[];
  className?: string;
};

export const ShowHealthRail: React.FC<Props> = ({
  data,
  compact = false,
  sections = ["health", "sellthrough"],
  className,
}) => {
  const { metrics, ticket_summary } = data;
  const projectedRevenue = metrics.projected_revenue ?? 0;
  const actualRevenue = metrics.actual_revenue ?? ticket_summary.revenue_generated ?? 0;
  const totalCost = metrics.total_cost ?? 0;
  const sellThrough = ticket_summary.sell_through_pct ?? 0;
  const ticketsSold = ticket_summary.tickets_sold ?? metrics.tickets_sold ?? 0;
  const totalTickets = ticket_summary.total_tickets ?? 0;

  const progressPct = useMemo(() => {
    if (!projectedRevenue) return 0;
    return Math.min(100, (actualRevenue / projectedRevenue) * 100);
  }, [actualRevenue, projectedRevenue]);

  const breakEvenPct = useMemo(() => {
    if (!projectedRevenue) return 0;
    const breakEvenRevenue = totalCost;
    return Math.min(100, (breakEvenRevenue / projectedRevenue) * 100);
  }, [projectedRevenue, totalCost]);

  const healthTone =
    actualRevenue >= totalCost && totalCost > 0 ? "good" : actualRevenue > 0 ? "amber" : "neutral";

  const showHealth = sections.includes("health");
  const showBudget = sections.includes("budget");
  const showSellThrough = sections.includes("sellthrough");
  const budget = metrics.budget_summary;
  // Over budget is the exception worth colouring; under or on plan is neutral.
  const budgetOver = Boolean(budget && budget.variance > 0);

  return (
    <div
      className={`show-health-rail${compact ? " show-health-rail--compact" : ""}${className ? ` ${className}` : ""}`}
    >
      {showHealth ? (
        <div className="show-health-rail__card">
          <div className="eventos-label">Show health</div>
          <div className={`show-health-rail__bar show-health-rail__bar--${healthTone}`}>
            <div className="show-health-rail__bar-track">
              <div className="show-health-rail__bar-fill" style={{ width: `${progressPct}%` }} />
              {actualRevenue > 0 && breakEvenPct > 0 && breakEvenPct < 100 ? (
                <div
                  className="show-health-rail__be-marker"
                  style={{ left: `${breakEvenPct}%` }}
                  title={`Break-even at ${formatINR(totalCost)}`}
                />
              ) : null}
            </div>
            <div className="show-health-rail__bar-label">
              {formatINR(actualRevenue)} of {formatINR(projectedRevenue)}
              {projectedRevenue > 0 ? ` · ${progressPct.toFixed(0)}%` : ""}
            </div>
            {totalCost > 0 && projectedRevenue > 0 ? (
              <div className="show-health-rail__bar-hint">Break-even at {formatINR(totalCost)}</div>
            ) : null}
          </div>
        </div>
      ) : null}

      {showBudget ? (
        <div className="show-health-rail__card">
          <div className="eventos-label">Budget summary</div>
          {budget ? (
            <>
              <p className="show-health-rail__budget-actual">{formatINR(budget.actual_total)}</p>
              <p className="show-health-rail__budget-est">
                of {formatINR(budget.estimated_total)} planned
              </p>
              <p
                className={`show-health-rail__budget-variance${
                  budgetOver ? " is-over" : ""
                }`}
              >
                {budget.variance === 0
                  ? "On budget"
                  : `${budgetOver ? "Over" : "Under"} by ${formatINR(Math.abs(budget.variance))}`}
                {/* Null percentage means nothing was budgeted, so there is no
                    baseline to be a percentage of. */}
                {budget.variance_pct !== null && budget.variance !== 0
                  ? ` · ${Math.abs(budget.variance_pct).toFixed(1)}%`
                  : ""}
              </p>
            </>
          ) : (
            <p className="show-health-rail__budget-est">No budget lines yet</p>
          )}
        </div>
      ) : null}

      {showSellThrough ? (
        <div className="show-health-rail__card">
          <div className="eventos-label">Sell-through</div>
          <p className="show-health-rail__sellthrough">
            {ticketsSold.toLocaleString("en-IN")} / {totalTickets.toLocaleString("en-IN")}
          </p>
          <p className="show-health-rail__sellthrough-pct">
            {formatSellThroughPct(sellThrough, ticketsSold)}
          </p>
        </div>
      ) : null}
    </div>
  );
};
